import {
    ltPromotionStatusesEnum,
    ltPromotionActionCodesEnum,
    innerPromotionActionTypesEnum,
} from '../enums/promotions.enum';
import { AppActionEnum } from '../enums/app-action.enum';
import { AppService } from './app.service';
import { playerSlice } from '../store/player/reducer';
import { loadGameAction } from '../store/casino/actions';
import { SegmentationsService } from './segmentations.service';
import { setPromotionsAmount, setPromotionsList } from '../store/promotions/reducer';
import { StringUtilService } from './util/string-util.service';

/**
 * Extracts the slug from a given URL.
 *
 * This function removes any trailing slashes from the provided URL and then retrieves the
 * last segment of the URL path. If the input URL is null or undefined, it returns null.
 *
 * @param {string} url - The URL string from which to extract the slug.
 * @returns {string|null} The extracted slug from the URL or null if the URL is empty or invalid.
 */
const getSlugFromUrl = (url) => {
    if (!url) {return null;}
    return url.replace(/\/+$/, '').split('/').pop();
};

/**
 * Transforms raw promotion data into a normalized promotion object with a standard structure.
 *
 * @returns {Object} A normalized promotion object containing formatted data fields.
 */
const normalizeBrandPromotion = (rawPromo) => {
    let target;

    switch (rawPromo.customButtonAction) {
    case innerPromotionActionTypesEnum.WIDGET:
        target = rawPromo.widgetAction;
        break;
    case innerPromotionActionTypesEnum.LINK:
        target = rawPromo.linkAction;
        break;
    case innerPromotionActionTypesEnum.GAME:
        target = rawPromo.gameId;
        break;
    default:
        target = null;
    }

    const terms =
        rawPromo?.essentialText?.trim() ||
        Dictionary.translate('termsAndConditionsPROMOTION') ||
        Dictionary.translate('RegulationTermsPostLogin');

    return {
        id: rawPromo.id,
        slug: getSlugFromUrl(rawPromo.promotionLink),
        orientation: rawPromo.vertical ?? 'casino',
        promotionDuration: null,
        images: rawPromo.images,
        content: {
            title: rawPromo.title,
            subtitle: rawPromo.subtitle,
            ctaText: rawPromo.customButtonText,
            terms
        },
        actions: {
            preLogin: {
                name: rawPromo.customButtonAction || ltPromotionActionCodesEnum.REGISTRATION,
                target: target || '',
            },
            postLogin: {
                name: rawPromo.customButtonAction || innerPromotionActionTypesEnum.LINK,
                target: target || rawPromo.promotionLink,
            },
        },
        scheduleIso: null,
        colors: {},
        nextPromotion: null,
        showOnPromotionsStrip: rawPromo.showOnPromotionsStrip,
    };
};

/**
 * Extract only the necessary fields from the API response
 * to reduce localStorage footprint and improve performance.
 */
const extractRelevantFields = (response) => {
    const promotions = response?.data?.promotions;
    if (!promotions) {return null;}
    const trimPromo = (promo) => {
        const {
            id,
            orientation,
            promotionDuration,
            images,
            content,
            actions,
            scheduleIso,
            colors,
            slug,
            nextPromotion,
            showOnPromotionsStrip
        } = promo;

        return {
            id,
            orientation,
            promotionDuration,
            images: {
                web: images?.web,
                mobile: images?.mobile
            },
            content: {
                title: content?.title,
                subtitle: content?.subtitle,
                ctaText: content?.ctaText,
                terms: content?.terms,
                countdownHeadline: content?.countdownHeadline,
                showCountdown: content?.showCountdown
            },
            actions: {
                preLogin: actions?.preLogin,
                postLogin: actions?.postLogin
            },
            scheduleIso,
            colors,
            slug,
            nextPromotion,
            showOnPromotionsStrip,
        };
    };

    return {
        main: promotions.main ? trimPromo(promotions.main) : null,
        secondary: Array.isArray(promotions.secondary)
            ? promotions.secondary.map(trimPromo)
            : []
    };
};

/**
 * Counts the number of the available promotions: LT + inner brand's.
 * @param {Object} promotionsList
 * @returns {*}
 */
const getPromotionsAmount = (promotionsList = {}) => {
    const countEntry = (entry) => {
        if (Array.isArray(entry)) {
            return entry.length;
        }
        if (entry && typeof entry === 'object') {
            return 1;
        }
        return 0;
    };

    const { main, secondary, brand } = promotionsList;

    return countEntry(main) + countEntry(secondary) + countEntry(brand);
};

/**
 * Fetches a list of brand promotions based on the current country and regulation.
 * Normalizes the fetched promotions for further usability.
 *
 * @return {Promise<Array>} A promise that resolves to an array of normalized brand promotions.
 * If an error occurs or the response is invalid, it returns an empty array.
 */
async function getBrandPromotions(country, regulation, playerSegments, signal) {
    try {
        const query = new URLSearchParams({
            action: 'getPromotionsList',
            country,
            regulation,
        }).toString();

        const res = await fetch(`/wp-admin/admin-ajax.php?${query}`, { signal });

        if (!res.ok) {
            throw new Error(`HTTP ${res.status} - ${res.statusText}`);
        }

        const response = await res.json();

        const promotions = Array.isArray(response?.promotions) ? response.promotions : [];
        const segmentedPromotions = SegmentationsService.filterPostsByPlayerSegments(promotions, playerSegments);

        return segmentedPromotions.map(promo => normalizeBrandPromotion(promo));
    } catch (err) {
        Tools.error('Failed to fetch brand promotions', err);
        return [];
    }
}

async function fetchPromotionsList(requestParams, abortSignal) {
    return new Promise((resolve, reject) => {
        SolarGatewayAPI.get(
            '/api/v1/promotions/limited-time',
            requestParams,
            (response) => {
                const trimmed = extractRelevantFields(response);
                resolve(trimmed);
            },
            null,
            abortSignal,
            (e) => {
                Tools.error('Promotions list request failed', e);
                reject(e);
            },
        );
    });
}

/**
 * Generates a unique cache key by combining a base key with serialized query parameters.
 *
 * @param {string} baseKey - The base string used as the key.
 * @param {Object} params - An object containing key-value pairs to be serialized as query parameters.
 * @returns {string} A string representing the generated cache key.
 */
export const generateCacheKey = (baseKey, params) => {
    const sortedKeys = Object.keys(params).sort((a, b) => a.localeCompare(b));
    const query = sortedKeys.map(k => `${k}=${StringUtilService.stringify(params[k])}`).join('&');
    return `${baseKey}?${query}`;
};

async function fetchFreshPromotions({
    requestData,
    playerSegments,
    cacheKey,
    cachedData,
    isSubscribed,
    setIsLoading,
    abortSignal,
    dispatch
}) {
    try {
        const [ltResult, wpResult] = await Promise.allSettled([
            fetchPromotionsList(requestData, abortSignal),
            getBrandPromotions(
                requestData.country,
                requestData.regulation,
                playerSegments ?? null,
                abortSignal
            ),
        ]);

        const freshResult = {
            main: ltResult.status === 'fulfilled' ? ltResult.value.main : null,
            secondary: ltResult.status === 'fulfilled' ? ltResult.value.secondary : [],
            brand: wpResult.status === 'fulfilled' ? wpResult.value : [],
        };

        const hasChanges = JSON.stringify(cachedData) !== JSON.stringify(freshResult);

        if (hasChanges && isSubscribed) {
            dispatch(setPromotionsList(freshResult));
            dispatch(setPromotionsAmount(getPromotionsAmount(freshResult)));
            LocalstorageTTLService.set(cacheKey, freshResult, 60 * 60 * 1000);
        }
    } catch (error) {
        Tools.error('Failed to fetch fresh promotions', error);
    } finally {
        if (isSubscribed) {
            setIsLoading(false);
        }
    }
}
export async function loadPromotions(params) {
    const {
        requestData,
        dispatch,
        setIsLoading,
        idleCallbackId,
    } = params;
    const cacheKey = generateCacheKey('ltAndBrandPromotions', requestData);
    params.cacheKey= cacheKey;

    const cachedData = LocalstorageTTLService.get(cacheKey);
    if (cachedData) {
        params.cachedData = cachedData;
        dispatch(setPromotionsList(cachedData));
        dispatch(setPromotionsAmount(getPromotionsAmount(cachedData)));
        setIsLoading(false);

        // Schedule a background update without blocking UI
        if ('requestIdleCallback' in window) {
            idleCallbackId.current = window.requestIdleCallback(() => {
                fetchFreshPromotions(params);
            }, { timeout: 10_000 });
        } else {
            // Fallback if unsupported
            idleCallbackId.current = window.setTimeout(() => {
                fetchFreshPromotions(params);
            }, 10_000);
        }
    } else {
        // No cache: fetch fresh immediately with loading indicator
        setIsLoading(true);
        await fetchFreshPromotions(params);
    }
}


export const PromotionsService = {
    /**
     * Filters the provided promotions array to return only promotions
     * that have the `show_on_promotions_strip` property set to true.
     *
     * @param {Array} promotions - An array of promotion objects to be filtered.
     * @return {Array} A filtered array of promotions meant to be shown on the promotions strip.
     */
    getStripPromotions(promotions) {
        if (!Array.isArray(promotions)) {return [];}

        return promotions.filter(
            (promo) => promo?.showOnPromotionsStrip === true
        );
    },

    /**
     * Filters the provided list of promotions to exclude those with an "EXPIRED" status.
     * A promotion without a schedule is included in the result.
     *
     * @param {Array} promotions - The array of promotion objects to be filtered.
     * @return {Array} - A filtered array of promotion objects excluding expired promotions.
     */
    filterPromotionsByStatus(promotions) {
        return promotions.filter((promo) => {
            if (!promo?.scheduleIso) {return true;} // no schedule, keep it
            const status = PromotionsService.getPromotionsStatus(promo.scheduleIso);
            return status !== ltPromotionStatusesEnum.EXPIRED;
        });
    },

    getFoldColorsVariables(colorSettings) {
        return 'brand' === colorSettings.colorTheme ? {} : {
            '--lt-promotions-thumbnail-main-bg-color': colorSettings.background,
            '--lt-promotions-thumbnail-text-color': colorSettings.text,
            '--lt-promotions-thumbnail-cta-bg-color': colorSettings.ctaBackground,
            '--lt-promotions-thumbnail-cta-color': colorSettings.ctaText,
            '--lt-promotions-thumbnail-clock-color': colorSettings.ctaCountdown,
            '--lt-promotions-thumbnail-tnc-color': colorSettings.questionsText,
        };
    },

    formatPromotionDate(dateObj, lang) {
        return new Intl.DateTimeFormat(
            lang ?? 'en-GB',
            { dateStyle: 'long' },
        ).format(dateObj);
    },

    getPromotionsStatus({
        liveModeStartDate,
        endDate,
    }) {
        // ongoing mode
        if (!liveModeStartDate && !endDate) {
            return ltPromotionStatusesEnum.ONGOING;
        }

        const now = new Date();
        const endDateParsed = new Date(endDate);

        // expired mode
        if (now > endDateParsed) {
            return ltPromotionStatusesEnum.EXPIRED;
        }

        if (liveModeStartDate) {
            const liveDateParsed = new Date(liveModeStartDate);

            // live mode
            if (liveDateParsed < now && now < endDateParsed) {
                return ltPromotionStatusesEnum.LIVE;
            }

            // countdown mode till Live start
            return ltPromotionStatusesEnum.COUNTDOWN_TILL_LIVE;
        }

        // countdown mode till expired
        return ltPromotionStatusesEnum.COUNTDOWN_TILL_END;
    },

    isCountdownState(status) {
        return [
            ltPromotionStatusesEnum.COUNTDOWN_TILL_END,
            ltPromotionStatusesEnum.COUNTDOWN_TILL_LIVE,
        ].includes(status);
    },

    isShowStateIndicator(status, isShowCountdown) {
        return !this.isCountdownState(status) || isShowCountdown;
    },

    execPromotionCta(actionObject, dispatch) {
        const ctaHandler = (action) => AppService.ctaHandler(action, actionObject.target, dispatch);

        const actionsMap = {
            // LT Promotion actions.
            [ltPromotionActionCodesEnum.REGISTRATION]: () => {
                dispatch(playerSlice.actions.showLoginOrRegistration());
            },
            [ltPromotionActionCodesEnum.GAME]: () => {
                dispatch(loadGameAction({
                    gameId: actionObject.target,
                }));
            },
            [ltPromotionActionCodesEnum.SHOW_DEPOSIT]: () => ctaHandler(AppActionEnum.DEPOSIT),
            [ltPromotionActionCodesEnum.PROVIDER_PAGE]: () => ctaHandler(AppActionEnum.GOTO),
            [ltPromotionActionCodesEnum.SPORT_LEAGUE]: () => ctaHandler(AppActionEnum.GOTO),
            // Inner promotion actions.
            [innerPromotionActionTypesEnum.LINK]: () => ctaHandler(AppActionEnum.GOTO),
            [innerPromotionActionTypesEnum.WIDGET]: () => ctaHandler(actionObject.target),
        };

        const action = actionsMap[actionObject.name];

        if (action) {
            action();
        }
    },

    getSecondaryPromotionsLayout(secondaryPromotions) {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const isLandScape = screenWidth > screenHeight;
        const isPortrait = screenHeight > screenWidth;

        // portrait + <1600 screens - all small items
        if (isPortrait || isLandScape && screenWidth < 1600) {
            return {
                bigThumbnails: [],
                smallThumbnails: secondaryPromotions,
            };
        }

        /**
         * Calculates how many big secondary promotions should be in layout
         * Short logic description:
         *  - In most cases layout should be split by 3 from the bottom to top
         *  - The top reminders from division by 3 are big thumbnails
         *
         * @link https://aspireglobal.atlassian.net/l/cp/d0zwmyNi#Secondary-promotions-layout
         */
        const calculateBigThumbnails = (promotionsAmount, origAmount = null) => {
            if (origAmount === null
                && (promotionsAmount < 3 || promotionsAmount === 4)) {
                return promotionsAmount;
            }

            const slice = promotionsAmount - 3;

            if (slice >= 3) {
                return calculateBigThumbnails(slice, promotionsAmount);
            }

            if (slice === 1) {
                return origAmount - 3;
            }

            if (slice === 0) {
                return 0;
            }

            // is even
            if (slice % 2 === 0) {
                return slice;
            }

            return slice;
        };

        const bigThumbnailsAmount = calculateBigThumbnails(secondaryPromotions.length);

        const bigThumbnails = secondaryPromotions.slice(0, bigThumbnailsAmount);
        const smallThumbnails = secondaryPromotions.slice(bigThumbnailsAmount);

        return {
            bigThumbnails,
            smallThumbnails,
        };
    },
};
